import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigInterface,
    ConnectivityService,
    ENDPOINTS,
    isDefined,
    MaterialCheckModel,
    MaterialQuoteModel,
    MaterialUtilitiesService,
    ModalClass,
    ODataMaterialCheckModel,
    ODataMaterialPricingResponseModel,
    ODataMatLTextModel,
    OnlineOnlyService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';
import { MaterialQuoteComponent } from '../material-quote/material-quote.component';
import { MaterialPricingModel } from './material-pricing.model';
import { faBan, faEllipsisV } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-material-pricing',
    templateUrl: './material-pricing.component.html',
    styleUrls: ['./material-pricing.component.scss'],
    standalone: false,
})
export class MaterialPricingComponent implements OnInit {
    @Input()
    public model: MaterialPricingModel;

    @Input()
    public displayRepairPrice = true;

    @Input()
    public displayCustomerPrice = true;

    @Input()
    public displayMaterialQuote: boolean;

    @Input()
    public enableMatOrderCheckOnlineOnly = false;

    @Input()
    public forceCustomerPricingTitle = true;

    @Output()
    public materialsConfirmed = new EventEmitter<MaterialCheckModel[]>();

    private readonly globalStorageLocation: string[];

    public isLoading = false;
    public faEllipsisV = faEllipsisV;
    public faBan = faBan;
    public strings: {
        header: string;
    };

    public materialPricingForm: FormGroup;
    public materialPricingFormChildren: FormArray;
    public isOnline$: Observable<boolean>;
    public enableAddMaterial$: Observable<boolean>;

    public displaySparePartsLinkAsMainActionButton: boolean;
    public displayCheckAsMainActionButton: boolean;
    public displayRequestAsMainActionButton: boolean;

    public constructor(
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        public readonly modalInstance: BsModalRef,
        private readonly modalService: BsModalService,
        private readonly appNotificationService: AppNotificationService,
        private readonly materialUtilities: MaterialUtilitiesService,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly connectivityService: ConnectivityService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.globalStorageLocation = this.configService.getGlobalStorageLocationMatPricing();
    }

    public ngOnInit(): void {
        this.materialPricingFormChildren = this.formBuilder.array([]);
        this.materialPricingForm = this.formBuilder.group({
            formChildren: this.materialPricingFormChildren,
        });

        this.isOnline$ = this.connectivityService.isOnline$;
        this.enableAddMaterial$ = this.isOnline$.pipe(
            map((isOnline) => !this.enableMatOrderCheckOnlineOnly || isOnline),
        );

        this.strings = {
            header:
                this.forceCustomerPricingTitle || this.displayRepairPrice
                    ? <string>this.translate.instant('MATERIAL_CUSTOMER_PRICING_TITLE')
                    : <string>this.translate.instant('MATERIAL_AVAILABILITY_TITLE'),
        };

        // Determines which will be the main action button to be displayed
        this.evaluateMainActionButton();
    }

    /**
     * This function adds a material to the checklist
     */
    public addMaterial(): void {
        this.model.addMaterialToPartSet();
        if (this.enableMatOrderCheckOnlineOnly) {
            this.checkMaterialPricing();
            return;
        }

        this.evaluateMainActionButton();
    }

    /**
     * This function deletes a material from the checklist
     *
     * @param index index of the material to be deleted
     */
    public deleteMaterial(index: number): void {
        this.model.deleteMaterialFromPartSet(index);
        this.evaluateMainActionButton();
    }

    public checkMaterialPricing(): void {
        this.isLoading = true;
        let filteredPartSetItem: ODataMaterialCheckModel[];

        this.onlineOnlyService
            .post<ODataMaterialPricingResponseModel>(ENDPOINTS.MatPricing, this.model.getOutModel())
            .pipe(
                take(1),
                map((data: ODataMaterialPricingResponseModel) => {
                    if (
                        isDefined(data.PartSet) &&
                        typeof data.PartSet === 'object' &&
                        Array.isArray(data.PartSet.results)
                    ) {
                        this.model.partSet.value = [];
                        filteredPartSetItem = data.PartSet.results;
                        if (this.globalStorageLocation.length > 0) {
                            filteredPartSetItem = data.PartSet.results.filter((material) =>
                                this.globalStorageLocation.includes(material[ConfigConstants.materialFilterKey]),
                            );
                        }

                        filteredPartSetItem.forEach((result) => {
                            this.populatePartSet(result, data.LtextSet.results);
                        });
                    }
                }),
                catchError((error) => {
                    this.log.error('MaterialPricingComponent: checkMaterialPricing: error', error);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CUSTOMER_PRICING_REQUEST_ERROR_TITLE'),
                        `${<string>this.translate.instant('MATERIAL_REQUEST_ERROR_BODY')} ${JSON.stringify(error)}`,
                    );

                    return of(undefined);
                }),
                finalize(() => {
                    this.isLoading = false;
                    this.evaluateMainActionButton();
                }),
            )
            .subscribe();
    }

    /**
     * This function creates MaterialCheckModel object from material and adds to partSet model
     *
     * @param material the material to be added
     * @param longTextSet the long text set
     */
    private populatePartSet(material: ODataMaterialCheckModel, longTextSet: ODataMatLTextModel[]): void {
        const resultedMaterial = new MaterialCheckModel(
            material.Material,
            parseInt(material.Quantity, 10),
            material.StorageLoc,
        );

        resultedMaterial.updateFromResponse(material, longTextSet);
        this.model.partSet.addModel(resultedMaterial);
    }

    /**
     * Confirms the current part set to the caller and closes the dialog. The caller decides what
     * happens next (e.g. opening the order screen, or merging the result into an order already open).
     */
    public order(): void {
        this.materialsConfirmed.next(
            this.materialUtilities.getMaterialsWithGlobalStorageLocation(this.model.partSet.value),
        );
        this.materialsConfirmed.complete();
        this.modalInstance.hide();
    }

    /**
     * Opens material quote
     */
    public createQuote(): void {
        // Once the send order is successful the modal should not be displayed.
        this.modalInstance.hide();
        this.modalService.show(MaterialQuoteComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new MaterialQuoteModel(
                    undefined,
                    this.model.notificationModel,
                    this.materialUtilities.getMaterialsWithGlobalStorageLocation(this.model.partSet.value),
                ),
            },
            ariaLabelledBy: 'material-quote-modal-title',
        });
    }

    /**
     *  Logic to handle which action will be displayed as the main
     *  action button for mobile view. Other actions will be displayed in the drop up menu
     */
    private evaluateMainActionButton(): void {
        this.displaySparePartsLinkAsMainActionButton = this.model.partSet.value.length === 0;
        this.displayCheckAsMainActionButton =
            !this.enableMatOrderCheckOnlineOnly &&
            this.model.partSet.value.length !== 0 &&
            this.model.partSet.value.some((part) => !part.available);
        this.displayRequestAsMainActionButton =
            this.model.partSet.value.length !== 0 &&
            (this.enableMatOrderCheckOnlineOnly || this.model.partSet.value.every((part) => part.available));
    }
}
